import React from 'react';

function isTableRow(line) {
  return /^\s*\|/.test(line || '');
}

function isTableSeparator(line) {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test((line || '').trim());
}

function parseTableCells(line) {
  const trimmed = (line || '').trim();
  const inner = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return inner.split('|').map((c) => c.trim());
}

/** Split markdown into block-level segments (cheap — safe to run every stream token). */
export function splitTeamAssistantMarkdownBlocks(source) {
  const text = source == null ? '' : String(source);
  if (!text) return [];

  const lines = text.split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (isTableRow(line)) {
      const tableLines = [];
      while (i < lines.length && isTableRow(lines[i])) {
        tableLines.push(lines[i]);
        i += 1;
      }
      blocks.push({ type: 'table', lines: tableLines });
      continue;
    }

    if (/^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length) {
        const l = lines[i];
        const bullet = /^\s*[-*]\s+/.test(l) || /^\s*\d+\.\s+/.test(l);
        if (!bullet) break;
        items.push(l.replace(/^\s*(?:[-*]|\d+\.)\s+/, ''));
        i += 1;
      }
      blocks.push({ type: 'list', ordered: /^\s*\d+\.\s+/.test(line), items });
      continue;
    }

    if (/^\s*$/.test(line)) {
      i += 1;
      continue;
    }

    const paraLines = [];
    while (i < lines.length) {
      const l = lines[i];
      if (/^\s*$/.test(l)) break;
      if (isTableRow(l)) break;
      if (/^\s*[-*]\s+/.test(l) || /^\s*\d+\.\s+/.test(l)) break;
      paraLines.push(l);
      i += 1;
    }
    blocks.push({ type: 'paragraph', text: paraLines.join('\n') });
  }

  return blocks;
}

function findNextInlineSpecial(source, start) {
  const indices = [source.indexOf('**', start), source.indexOf('`', start)];
  const linkRel = source.slice(start).search(/\[([^\]]*)\]\(([^)\s]+)\)/);
  const candidates = indices.filter((n) => n >= 0);
  if (linkRel >= 0) candidates.push(start + linkRel);
  return candidates.length ? Math.min(...candidates) : -1;
}

function renderInline(text, keyPrefix = 'i') {
  const source = text == null ? '' : String(text);
  if (!source) return null;

  const nodes = [];
  let i = 0;
  let key = 0;

  const pushText = (t) => {
    if (t) nodes.push(<React.Fragment key={`${keyPrefix}-t-${key++}`}>{t}</React.Fragment>);
  };

  while (i < source.length) {
    if (source.startsWith('**', i)) {
      const end = source.indexOf('**', i + 2);
      if (end === -1) {
        pushText(source.slice(i));
        break;
      }
      nodes.push(
        <strong key={`${keyPrefix}-b-${key++}`}>{renderInline(source.slice(i + 2, end), `${keyPrefix}-${key}`)}</strong>
      );
      i = end + 2;
      continue;
    }

    if (source[i] === '`') {
      const end = source.indexOf('`', i + 1);
      if (end === -1) {
        pushText(source.slice(i));
        break;
      }
      nodes.push(
        <code key={`${keyPrefix}-c-${key++}`} className="team-assistant-md-inline-code">
          {source.slice(i + 1, end)}
        </code>
      );
      i = end + 1;
      continue;
    }

    const linkMatch = source.slice(i).match(/^\[([^\]]*)\]\(([^)\s]+)\)/);
    if (linkMatch) {
      nodes.push(
        <span key={`${keyPrefix}-a-${key++}`} className="team-assistant-md-plain-link">
          {linkMatch[1] || linkMatch[2]}
        </span>
      );
      i += linkMatch[0].length;
      continue;
    }

    const nextSpecial = findNextInlineSpecial(source, i);

    if (nextSpecial === -1) {
      pushText(source.slice(i));
      break;
    }
    if (nextSpecial > i) {
      pushText(source.slice(i, nextSpecial));
      i = nextSpecial;
      continue;
    }
  }

  return nodes.length === 1 ? nodes[0] : nodes;
}

function renderTableBlock(lines, blockKey) {
  if (!lines?.length) return null;

  let headerCells = parseTableCells(lines[0]);
  let bodyStart = 1;

  if (lines.length > 1 && isTableSeparator(lines[1])) {
    bodyStart = 2;
  } else if (lines.length === 1) {
    return (
      <p key={blockKey} className="team-assistant-md-para">
        {renderInline(lines[0], blockKey)}
      </p>
    );
  }

  const bodyRows = lines.slice(bodyStart).map(parseTableCells);
  const colCount = Math.max(headerCells.length, ...bodyRows.map((r) => r.length), 1);

  const pad = (cells) => {
    const row = [...cells];
    while (row.length < colCount) row.push('');
    return row.slice(0, colCount);
  };

  headerCells = pad(headerCells);

  return (
    <div key={blockKey} className="team-assistant-md-table-wrap">
      <table>
        <thead>
          <tr>
            {headerCells.map((cell, ci) => (
              <th key={`${blockKey}-h-${ci}`}>{renderInline(cell, `${blockKey}-h-${ci}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, ri) => (
            <tr key={`${blockKey}-r-${ri}`}>
              {pad(row).map((cell, ci) => (
                <td key={`${blockKey}-r-${ri}-c-${ci}`}>{renderInline(cell, `${blockKey}-r-${ri}-c-${ci}`)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Fast markdown subset for assistant replies (incremental during SSE — no full AST rebuild).
 */
export function TeamAssistantLiteMarkdownBody({ content }) {
  const blocks = splitTeamAssistantMarkdownBlocks(content);

  if (!blocks.length) {
    return null;
  }

  return blocks.map((block, index) => {
    const key = `b-${index}`;

    if (block.type === 'table') {
      return renderTableBlock(block.lines, key);
    }

    if (block.type === 'list') {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag key={key}>
          {block.items.map((item, ii) => (
            <li key={`${key}-${ii}`}>{renderInline(item, `${key}-${ii}`)}</li>
          ))}
        </Tag>
      );
    }

    return (
      <p key={key} className="team-assistant-md-para">
        {renderInline(block.text, key)}
      </p>
    );
  });
}
